// pages/secure/ContactCompliancePage.js — the do-not-contact record, visible at last.
//
// /api/contact-compliance has had ten working endpoints and a nightly cron for
// some time, and no screen at all. So the brokerage was performing a do-not-call
// obligation continuously and could not inspect, correct or evidence it from the
// portal. That is the one gap in this application where the absence of a screen
// was itself the compliance exposure, which is why this page exists.
//
// Four things a person actually needs to do here:
//
//   1. CHECK a number before ringing it. The fastest path on the page, because it
//      is the thing that happens before a call rather than after a complaint.
//   2. SEE who has asked not to be contacted, and add someone who just did.
//   3. KNOW whether the registry data is current. A number cannot be scrubbed
//      against a list the brokerage does not hold, and stale is nearly as bad as
//      absent — so staleness is shown as a state, not buried in a date.
//   4. LIFT an entry, which is the managing broker's alone and needs a written
//      reason. The row is deactivated, never deleted: the record that somebody
//      once asked has to survive the decision to contact them again.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import DncRegistryPanel from '../../components/secure/DncRegistryPanel';
import FloridaDncPanel from '../../components/secure/FloridaDncPanel';
import OptedOutSendsPanel from '../../components/secure/OptedOutSendsPanel';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';

const input = 'block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-slate-500 focus:border-slate-500';
const label = 'block text-sm font-medium text-gray-700 mb-1';

const ContactCompliancePage = () => {
    const api = useApiClient();
    const [registry, setRegistry] = useState(null);
    const [suppressions, setSuppressions] = useState([]);
    const [q, setQ] = useState('');
    const [channel, setChannel] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    // The pre-call check.
    const [checkValue, setCheckValue] = useState('');
    const [checkResult, setCheckResult] = useState(null);
    const [checking, setChecking] = useState(false);
    const [checkFlorida, setCheckFlorida] = useState(false);   // contact's address is in Florida

    const [adding, setAdding] = useState(null);
    const [lifting, setLifting] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const qs = new URLSearchParams();
            if (q) qs.set('q', q);
            if (channel) qs.set('channel', channel);
            const [reg, sup] = await Promise.all([
                api.get('/api/contact-compliance/registry/status'),
                api.get(`/api/contact-compliance/suppressions?${qs}`),
            ]);
            setRegistry(reg);
            setSuppressions(sup);
            setError('');
        } catch (e) { setError(e.message || 'Could not load the contact compliance record.'); }
    }, [api, q, channel]);
    useEffect(() => { load(); }, [load]);

    const runCheck = async () => {
        if (!checkValue.trim()) return;
        setChecking(true); setCheckResult(null); setError('');
        try {
            const isEmail = checkValue.includes('@');
            setCheckResult(await api.post('/api/contact-compliance/check',
                isEmail ? { email: checkValue.trim() } : { phone: checkValue.trim(), state: checkFlorida ? 'FL' : null }));
        } catch (e) { setError(e.message); } finally { setChecking(false); }
    };

    const addSuppression = async () => {
        setBusy(true); setError(''); setNotice('');
        try {
            await api.post('/api/contact-compliance/suppressions', adding);
            setAdding(null);
            setNotice('Recorded. They will not be contacted on that channel.');
            load();
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const lift = async () => {
        setBusy(true); setError(''); setNotice('');
        try {
            await api.delete(`/api/contact-compliance/suppressions/${lifting.suppression_id}`,
                { reason: lifting.reason });
            setLifting(null);
            setNotice('Lifted. The record of the original request is kept.');
            load();
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const allowed = checkResult && checkResult.allowed;

    return (
        <div className="p-6 max-w-5xl">
            <h1 className="text-2xl font-bold text-gray-900">Contact Compliance</h1>
            <p className="text-sm text-gray-500 mb-4">
                Do-not-call and do-not-email records, and the federal registry the brokerage scrubs
                against. Suppression is honoured on every channel the office uses.
            </p>

            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            {notice && <p className="mb-3 text-sm text-green-700">{notice}</p>}

            {/* 1. Check before ringing */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
                <h2 className="font-semibold text-slate-800">May we contact this person?</h2>
                <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    A telephone number or an email address. Check before the call, not after the complaint.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                    <input className={`${input} max-w-xs`} value={checkValue} placeholder="847-749-4270"
                        onChange={(e) => setCheckValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && runCheck()} />
                    <button onClick={runCheck} disabled={checking || !checkValue.trim()}
                        className="px-4 py-2 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300">
                        {checking ? 'Checking…' : 'Check'}
                    </button>
                    <label className="text-xs text-slate-600 flex items-center gap-1" title="Brings in the Florida Do Not Call list and Florida's calling hours even for a non-Florida area code">
                        <input type="checkbox" checked={checkFlorida} onChange={(e) => setCheckFlorida(e.target.checked)} />
                        Address is in Florida
                    </label>
                </div>
                {checkResult && (
                    <div className={`mt-3 p-3 rounded-md border ${allowed
                        ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <p className={`text-sm font-semibold ${allowed ? 'text-green-800' : 'text-red-800'}`}>
                            {allowed ? 'No record against this contact.' : 'Do not contact.'}
                        </p>
                        {/* reasons are objects: { code, message, authority, blocks }.
                            The blocking ones are the answer; the notes are context. */}
                        {Array.isArray(checkResult.reasons) && checkResult.reasons.map((r, i) => (
                            <p key={r.code || i}
                               className={`text-xs mt-1 ${r.blocks
                                   ? 'text-red-800 font-medium' : (allowed ? 'text-green-700' : 'text-red-600')}`}>
                                {r.message}
                                {r.authority && <span className="font-normal opacity-70"> — {r.authority}</span>}
                            </p>
                        ))}
                    </div>
                )}
            </div>

            {/* 3. Registry currency */}
            {registry && (
                <div className={`border rounded-lg p-4 mb-4 ${
                    registry.loaded === 0 ? 'bg-red-50 border-red-200'
                        : registry.stale > 0 ? 'bg-amber-50 border-amber-200'
                        : 'bg-white border-slate-200'}`}>
                    <h2 className="font-semibold text-slate-800">Federal registry</h2>
                    {registry.loaded === 0 ? (
                        <p className="text-sm text-red-800 mt-1">{registry.note}</p>
                    ) : (
                        <p className="text-sm text-slate-700 mt-1">
                            {registry.loaded} area code{registry.loaded === 1 ? '' : 's'} loaded
                            {registry.stale > 0
                                ? <span className="text-amber-900 font-medium">
                                    {' '}· {registry.stale} older than {registry.max_age_days} days and
                                    must be refreshed before prospecting against them
                                  </span>
                                : <span className="text-green-800"> · all current</span>}
                        </p>
                    )}
                    {registry.area_codes?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {registry.area_codes.map((a) => (
                                <span key={a.area_code}
                                    className={`px-2 py-0.5 rounded text-xs ${a.stale
                                        ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-600'}`}
                                    title={`${a.numbers} numbers · ${a.age_days} days old`}>
                                    {a.area_code}{a.stale ? ' ⚠' : ''}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* 3b. Subscription, certification, refresh — the managing broker's controls */}
            <DncRegistryPanel onChanged={load} />
            <FloridaDncPanel onChanged={load} />

            {/* 2. The list */}
            <div className="bg-white border border-slate-200 rounded-lg">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                    <h2 className="font-semibold text-slate-800">Do-not-contact list</h2>
                    <div className="flex items-center gap-2">
                        <input className={`${input} max-w-[200px]`} value={q} placeholder="Search the whole list…"
                            onChange={(e) => setQ(e.target.value)} />
                        <select className={input} value={channel} onChange={(e) => setChannel(e.target.value)}>
                            <option value="">All channels</option>
                            <option value="all">All</option>
                            <option value="phone">Phone</option>
                            <option value="email">Email</option>
                            <option value="sms">SMS</option>
                        </select>
                        <button onClick={() => setAdding({ phone: '', email: '', channel: 'all',
                            source: 'consumer_request', evidence: '' })}
                            className="whitespace-nowrap px-3 py-2 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900">
                            + Add
                        </button>
                    </div>
                </div>
                <div className="p-4"><DataList rows={suppressions} rowKey="suppression_id" csvName="do-not-contact" defaultSort={{ key: 'requested_at', dir: 'desc' }}
                    empty={`Nobody has asked not to be contacted${q || channel ? ' matching that filter' : ''}.`}
                    note="The search box above looks through the whole list on the server (newest 1,000 matches shown); this box narrows the rows shown."
                    filters={[{ key: 'channel', label: 'Channel' }, { key: 'source', label: 'Source' }]}
                    columns={[
                        { key: 'value', label: 'Contact', value: (s) => s.value_normalized || s.value, render: (s) => <span className="font-medium">{s.value_normalized || s.value}</span> },
                        { key: 'channel', label: 'Channel' },
                        { key: 'source', label: 'Source' },
                        { key: 'client_name', label: 'Client', render: (s) => s.client_name || '—' },
                        { key: 'requested_at', label: 'Asked', type: 'date' },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (s) => <button onClick={() => setLifting({ ...s, reason: '' })} className="text-slate-500 hover:underline text-xs">Lift…</button> },
                    ]} /></div>
            </div>

            {getCurrentUser()?.isManagingBroker && <OptedOutSendsPanel />}

            {adding && (
                <Modal open size="md" title="Record a do-not-contact request" onClose={() => setAdding(null)}
                    footer={<>
                        <button onClick={() => setAdding(null)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                        <button onClick={addSuppression} disabled={busy || (!adding.phone && !adding.email)}
                            className="px-4 py-2 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300">
                            {busy ? 'Recording…' : 'Record'}
                        </button>
                    </>}>
                    <div className="space-y-3">
                        <div><label className={label}>Telephone</label>
                            <input className={input} value={adding.phone}
                                onChange={(e) => setAdding({ ...adding, phone: e.target.value })} /></div>
                        <div><label className={label}>Email</label>
                            <input className={input} value={adding.email}
                                onChange={(e) => setAdding({ ...adding, email: e.target.value })} /></div>
                        <p className="text-xs text-slate-500">One of the two is enough.</p>
                        <div><label className={label}>Channel</label>
                            <select className={input} value={adding.channel}
                                onChange={(e) => setAdding({ ...adding, channel: e.target.value })}>
                                <option value="all">Everything</option>
                                <option value="phone">Phone only</option>
                                <option value="email">Email only</option>
                                <option value="sms">SMS only</option>
                            </select></div>
                        <div><label className={label}>How they asked</label>
                            <input className={input} value={adding.evidence} placeholder="e.g. said so on a call, 24 Sep"
                                onChange={(e) => setAdding({ ...adding, evidence: e.target.value })} />
                            <p className="text-xs text-slate-500 mt-1">
                                Worth a sentence. If the request is ever questioned, this is the answer.
                            </p></div>
                    </div>
                </Modal>
            )}

            {lifting && (
                <Modal open size="md" title="Take someone off the do-not-contact list"
                    onClose={() => setLifting(null)}
                    footer={<>
                        <button onClick={() => setLifting(null)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
                        <button onClick={lift} disabled={busy || !lifting.reason.trim()}
                            className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300">
                            {busy ? 'Lifting…' : 'Lift the suppression'}
                        </button>
                    </>}>
                    <p className="text-sm text-slate-700">
                        <strong>{lifting.value_normalized || lifting.value}</strong> asked not to be contacted
                        {lifting.requested_at ? ` on ${new Date(lifting.requested_at).toLocaleDateString()}` : ''}.
                    </p>
                    <p className="text-sm text-slate-600 mt-2">
                        This is the managing broker’s decision and it needs a reason. The usual lawful reason is
                        that they have asked to be contacted again, in writing. The original request is kept on
                        the record either way.
                    </p>
                    <div className="mt-3">
                        <label className={label}>Reason</label>
                        <input className={input} value={lifting.reason} autoFocus
                            placeholder="e.g. emailed on 24 Sep asking us to call about 3000 MacArthur"
                            onChange={(e) => setLifting({ ...lifting, reason: e.target.value })} />
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ContactCompliancePage;
